import { SceneData } from './SceneExporter';
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { File3dm } from 'rhino3dm';
import rhino3dm from 'rhino3dm';

import Grid from '../Geodata/grid';
import { getGrid } from 'store/LoadingStore';
import { RepaintProgress } from '../Gui/components/panels/ProgressBar';
import { SensorData } from 'Simulations/SensorPoint';
import type { CoordinateSystemSelection } from '@visualizer/types';

import { computeParam1, computeParam2 } from './UtilityFunctions';

export const rhino = await rhino3dm();

/**
 * Export3DRhino.ts
 *
 * Goal:
 * - Convert Three.js scene export (SceneData) into a Rhino .3dm file.
 *
 * Key rules:
 * - Rhino will show VERTEX COLORS if they exist, and they can override object/material colors.
 * - Sensors are special: their "heatmap" is vertex colors → we must keep them.
 * - Buildings are often multi-material (geometry.groups + material[]) → we must export one Rhino mesh per group.
 *
 * Result:
 * - Normal meshes: exported with Rhino materials + objectColor (and NO vertex colors).
 * - Sensors: exported with vertex colors preserved.
 * - Multi-material meshes: split into multiple Rhino meshes, each with its own material/objectColor.
 */

// ------------------------------------------------------------
// Small utilities
// ------------------------------------------------------------

// Cache Rhino materials to avoid duplicates.
// key = "r,g,b" -> materialIndex
const rhinoMaterialCache = new Map<string, number>();

function toRhinoColorArray(color: THREE.Color): number[] {
    // Rhino expects [0..255] RGBA
    return [Math.round(color.r * 255), Math.round(color.g * 255), Math.round(color.b * 255), 255];
}

function toRhinoColorObj(color: THREE.Color) {
    // Rhino PhysicallyBased().baseColor needs floats, but ObjectAttributes.objectColor uses 0..255.
    return { r: Math.round(color.r * 255), g: Math.round(color.g * 255), b: Math.round(color.b * 255) };
}

function getOrCreateRhinoMaterialIndex(rhinoScene: File3dm, baseColor: THREE.Color, nameHint: string): number {
    const key = `${baseColor.r.toFixed(4)},${baseColor.g.toFixed(4)},${baseColor.b.toFixed(4)}`;
    const cached = rhinoMaterialCache.get(key);
    if (cached !== undefined) return cached;

    const rhMat = new rhino.Material();
    rhMat.name = `${nameHint || 'mat'}_${key}`;

    // “classic” color
    rhMat.diffuseColor = toRhinoColorArray(baseColor);

    // PBR base color
    rhMat.toPhysicallyBased();
    rhMat.physicallyBased().baseColor = { r: baseColor.r, g: baseColor.g, b: baseColor.b, a: 1 };

    rhinoScene.materials().add(rhMat);
    const idx = rhinoScene.materials().count - 1;
    rhinoMaterialCache.set(key, idx);

    return idx;
}

async function yieldToBrowser() {
    // let input events (click) get processed
    await new Promise<void>((r) => setTimeout(r, 0));
}

// Attach User Text to the Sensor Points
function writeSensorUserText(mesh: THREE.Mesh, attr: any, sensorById: Map<number, SensorData>, valueUnit: string) {
    const m = (mesh.name || '').match(/^Sensor(\d+)$/);
    if (!m) {
        console.log('[SensorUserText] MISS (bad name)', mesh.name);
        return;
    }

    const id = Number(m[1]);
    const sensorData = sensorById.get(id);

    if (!sensorData) {
        console.log('[SensorUserText] MISS (no sensor for id)', id, 'mesh', mesh.name);
        return;
    }

    //console.log('[SensorUserText] Hit (sensor for id)', id, 'mesh', mesh.name);

    const sp = sensorData.getSensorPoint();
    const dir = sensorData.getVectorialResult();

    attr.setUserString('ID', String(sensorData.getID()));
    attr.setUserString('Index', String(sp?.index));
    attr.setUserString('Floor', String(sp?.getFloor?.()));

    attr.setUserString('Value', `${fmt3(sensorData.getScalarValue())}${valueUnit ? ' ' + valueUnit : ''}`);
    attr.setUserString('Dir theta', `${fmt3(computeParam1(dir))} °`);
    attr.setUserString('Dir phi', `${fmt3(computeParam2(dir))} °`);
}

// Formatter to trim the values after 3 decimals
function fmt3(n: unknown): string {
    const v = typeof n === 'number' ? n : Number(n);
    if (!Number.isFinite(v)) return '';
    // toFixed returns a string already
    return v.toFixed(3);
}

// ------------------------------------------------------------
// Color extraction
// ------------------------------------------------------------

/**
 * Returns a single “base” color for a mesh.
 *
 * Deterministic rules:
 * 1) If the chosen material has `.color` → use it
 * 2) Else if the geometry has vertex colors → use the first vertex color (fallback)
 * 3) Else → null
 *
 * Notes:
 * - Sensors rely on vertex colors; non-sensors should not
 * - For multi-material meshes we pass a material override.
 */
function getMeshColor(mesh: THREE.Mesh, materialOverride?: any): THREE.Color | null {
    const matAny = materialOverride ?? (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material);
    if (!matAny) return null;

    if ((matAny as any).color) {
        return (matAny as any).color as THREE.Color;
    }

    const geometry = mesh.geometry as THREE.BufferGeometry;
    const colAttr = geometry?.getAttribute?.('color') as any;
    if (colAttr && colAttr.count > 0) {
        const c = new THREE.Color();
        c.fromBufferAttribute(colAttr, 0);
        return c;
    }

    return null;
}

// ------------------------------------------------------------
// Export: lines
// ------------------------------------------------------------

/**
 * Export LineSegments2 into Rhino as a polyline curve.
 *
 * We deduplicate points because LineSegments2 stores repeated vertices.
 */
function Export3DLines(lineMesh: LineSegments2, rhinoScene: File3dm, parentLayerIndex: number) {
    const geometry = lineMesh.geometry as any;

    if (!geometry?.attributes?.instanceStart) {
        console.warn('LineSegments2 has no geometry positions.');
        return;
    }

    const positions = geometry.attributes.instanceStart.data.array;

    const pointMap = new Map<string, number>();
    const points: number[][] = [];

    for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i];
        const y = positions[i + 1];
        const z = positions[i + 2];

        const point = [x, z, -y];

        const key = point.map((coord) => coord.toFixed(6)).join(',');

        if (!pointMap.has(key)) {
            pointMap.set(key, points.length);
            points.push(point);
        }
    }

    if (points.length < 2) return;

    const polyline = new rhino.Polyline(points.length);
    for (const pt of points) polyline.add(pt[0], pt[1], pt[2]);

    const curve = polyline.toNurbsCurve();

    const attributes = new rhino.ObjectAttributes();
    attributes.layerIndex = parentLayerIndex;

    rhinoScene.objects().add(curve, attributes);
}

// ------------------------------------------------------------
// Export: meshes
// ------------------------------------------------------------

/**
 * Export a Three.js Mesh into Rhino.
 *
 * Handles 3 important cases:
 * - Sensor meshes: keep vertex colors (heatmap)
 * - Multi-material meshes: split by geometry.groups, export one Rhino mesh per group
 * - Normal meshes: one Rhino mesh
 */
async function Export3DMMesh(
    mesh: THREE.Mesh,
    rhinoScene: File3dm,
    parentLayerIndex: number,
    defaultBlackIndex: number,
    throwIfCancelled: () => void,
    sensorById: Map<number, SensorData>,
    valueUnit: string
) {
    throwIfCancelled();

    mesh.updateMatrixWorld(true);

    // Basic safety: Rhino conversion needs positions
    const geom = mesh.geometry as THREE.BufferGeometry;
    const posAttr = geom?.getAttribute?.('position') as any;
    if (!posAttr?.array || posAttr.count === 0) {
        console.warn('[RhinoExport] Skipping mesh with no valid position attribute:', mesh.name, mesh.type, geom);
        return;
    }

    const cloneGeometry = geom.clone();

    const isSensor = (mesh.name || '').startsWith('Sensor');

    // IMPORTANT:
    // - Sensors: vertex colors are the data → keep them.
    // - Everything else: vertex colors can override Rhino materials → remove them.
    if (!isSensor && cloneGeometry.getAttribute('color')) {
        cloneGeometry.deleteAttribute('color');
    }

    const hasGroups = Array.isArray(mesh.material) && cloneGeometry.groups && cloneGeometry.groups.length > 0;

    // -----------------------------
    // Case A: Multi-material mesh
    // -----------------------------
    if (hasGroups) {
        const mats = mesh.material as any[];

        for (let gi = 0; gi < cloneGeometry.groups.length; gi++) {
            throwIfCancelled();

            // yield every few groups
            if (gi % 5 === 0) await yieldToBrowser();

            const g = cloneGeometry.groups[gi];

            // Group.materialIndex can be undefined → default to 0
            const mi = g.materialIndex ?? 0;
            const mat = mats[mi] ?? mats[0];

            const color = getMeshColor(mesh, mat);
            const materialIndex = color ? getOrCreateRhinoMaterialIndex(rhinoScene, color, mesh.name) : defaultBlackIndex;

            const attr = new rhino.ObjectAttributes();
            attr.name = `${mesh.name || 'Mesh'}_g${gi}_m${mi}`;
            attr.layerIndex = parentLayerIndex;

            if (isSensor) writeSensorUserText(mesh, attr, sensorById, valueUnit);

            // Always assign a Rhino material (for Rendered mode)
            attr.materialIndex = materialIndex;
            attr.materialSource = rhino.ObjectMaterialSource.MaterialFromObject;

            // Also assign objectColor (for Shaded mode)
            if (color) {
                attr.colorSource = rhino.ObjectColorSource.ColorFromObject;
                attr.objectColor = toRhinoColorObj(color) as any;
            } else {
                attr.colorSource = rhino.ObjectColorSource.ColorFromLayer;
            }

            // Isolate only this group’s triangles
            const partGeom = cloneGeometry.clone();

            if (!partGeom.index) {
                console.warn('[RhinoExport] groups exist but geometry has no index', mesh.name);
                partGeom.dispose();
                continue;
            }

            const indexArray = partGeom.index.array as any;
            const sliced = indexArray.slice(g.start, g.start + g.count);

            // Rhino exporter is happier with Uint32 indices
            const slicedTyped =
                sliced instanceof Uint32Array
                    ? sliced
                    : sliced instanceof Uint16Array
                      ? new Uint32Array(sliced)
                      : new Uint32Array(Array.from(sliced));

            partGeom.setIndex(new THREE.BufferAttribute(slicedTyped, 1));

            // Remove vertex colors on building parts
            if (partGeom.getAttribute('color')) partGeom.deleteAttribute('color');

            throwIfCancelled();

            const rhPart = rhino.Mesh.createFromThreejsJSON({ data: partGeom } as any);

            // Clear vertex colors in Rhino too (extra safety)
            try {
                rhPart.vertexColors().clear();
            } catch (e) {}

            rhinoScene.objects().add(rhPart, attr);

            partGeom.dispose();
        }

        cloneGeometry.dispose();
        return;
    }

    // -----------------------------
    // Case B: Single-material mesh
    // -----------------------------
    const attr = new rhino.ObjectAttributes();
    attr.name = mesh.name;
    attr.layerIndex = parentLayerIndex;

    if (isSensor) writeSensorUserText(mesh, attr, sensorById, valueUnit);

    const color = getMeshColor(mesh);
    const materialIndex = color ? getOrCreateRhinoMaterialIndex(rhinoScene, color, mesh.name) : defaultBlackIndex;

    attr.materialIndex = materialIndex;
    attr.materialSource = rhino.ObjectMaterialSource.MaterialFromObject;

    if (color) {
        attr.colorSource = rhino.ObjectColorSource.ColorFromObject;
        attr.objectColor = toRhinoColorObj(color) as any;
    } else {
        attr.colorSource = rhino.ObjectColorSource.ColorFromLayer;
    }

    throwIfCancelled();

    const rhMesh = rhino.Mesh.createFromThreejsJSON({ data: cloneGeometry } as any);

    // Non-sensors must not carry vertex colors into Rhino (override issue)
    if (!isSensor) {
        try {
            rhMesh.vertexColors().clear();
        } catch (e) {}
    }

    rhinoScene.objects().add(rhMesh, attr);
    cloneGeometry.dispose();
}

// ------------------------------------------------------------
// Main export function
// ------------------------------------------------------------

/**
 * Export SceneData into Rhino 3DM.
 *
 * What it creates:
 * - Layers following SceneData.layerGroupNames and layerNames
 * - Curves for LineSegments2
 * - Meshes for THREE.Mesh, with correct color behavior:
 *   - sensors: keep vertex colors
 *   - normal meshes: use Rhino materials + objectColor (no vertex colors)
 *   - multi-material meshes: split per group
 */
export async function Export3DRhino(
    scenedata: SceneData,
    setProgress: (progress: number | undefined) => void,
    isCancelled: () => boolean,
    coordinateSystem?: CoordinateSystemSelection
): Promise<{ formattedData: Uint8Array; extension?: string }> {
    console.log('Exporting to Rhino 3DM format');

    // Cancellation helper
    const throwIfCancelled = () => {
        if (isCancelled()) {
            console.debug('Export cancelled by user');
        }
    };

    // One export = one cache
    rhinoMaterialCache.clear();

    const rhinoScene = new rhino.File3dm();
    rhinoScene.applicationName = 'UrbanMetrix';
    rhinoScene.applicationUrl = 'https://urbanmetrix.com/';
    rhinoScene.startSectionComments = 'Contains the layer generated from geodata.' + scenedata.geoDataName;

    const activeLayer = getGrid().getActiveLayer();

    // Sensors from the pipeline ONLY
    const pipelineResult = activeLayer.runPipeline?.({ suppressNotifications: true, octTree: getGrid().octTree! });
    const sensorsFromPipeline: SensorData[] = pipelineResult?.data ?? [];

    // Unit
    const activeFrame = activeLayer.getActiveFrame();
    const valueUnit = activeFrame.parentLayer.getResult()?.unitOfMeasure ?? 'unknown unit';

    // Map id -> sensor
    const sensorById = new Map<number, SensorData>();
    for (const s of sensorsFromPipeline) {
        if (!s) continue;

        const id = s.getID?.() ?? (s as any).id;
        if (id == null) continue;

        sensorById.set(Number(id), s);
    }

    // Default fallback material (used when we can't read a color)
    const defaultBlack = new rhino.Material();
    defaultBlack.name = 'Default_Black';
    defaultBlack.diffuseColor = [0, 0, 0, 255];
    defaultBlack.toPhysicallyBased();
    defaultBlack.physicallyBased().baseColor = { r: 0, g: 0, b: 0, a: 1 };

    rhinoScene.materials().add(defaultBlack);
    const defaultBlackIndex = rhinoScene.materials().count - 1;

    // check for cancellation
    throwIfCancelled();

    // Neutral layer color (Rhino layer list)
    const LAYER_COLOR = { r: 220, g: 220, b: 220, a: 255 };

    // Geo anchor / units
    const settings = rhinoScene.settings();

    const anchor = settings.earthAnchorPoint;
    anchor.earthBasepointLatitude = Grid.siteLat;
    anchor.earthBasepointLongitude = Grid.siteLon;
    anchor.earthBasepointElevation = Grid.siteAlt;
    settings.earthAnchorPoint = anchor;

    settings.modelUnitSystem = rhino.UnitSystem.Meters;
    settings.pageUnitSystem = rhino.UnitSystem.Meters;

    const exportOrigin =
        coordinateSystem?.mode === 'custom'
            ? [coordinateSystem.centerX, coordinateSystem.centerY, coordinateSystem.centerZ]
            : [Grid.gridOrigin.x, Grid.gridOrigin.y, Grid.gridOrigin.z];

    settings.modelBasePoint = exportOrigin;
    rhinoScene.strings().set('Site name: ', getGrid().name);
    rhinoScene.strings().set('Real World Origin', `x: ${exportOrigin[0]} y: ${exportOrigin[1]} z: ${exportOrigin[2]}`);
    rhinoScene.strings().set('Coordinates', 'lat: ' + Grid.siteLat + ' lon: ' + Grid.siteLon + ' alt: ' + Grid.siteAlt);

    // Root layer for this export
    const parentLayerIndex = rhinoScene.layers().addLayer(scenedata.geoDataName, { r: 255, g: 255, b: 255, a: 255 });
    const parentLayer = rhinoScene.layers().findIndex(parentLayerIndex);
    const parentLayerId = parentLayer.id;

    // Optional sensor layer
    let sensorLayerId = '';
    if (Object.keys(scenedata.metaData).length) {
        const sensorLayerIndex = rhinoScene.layers().addLayer('Sensor Data', LAYER_COLOR);
        const sensorLayer = rhinoScene.layers().findIndex(sensorLayerIndex);
        sensorLayerId = sensorLayer.id;
        sensorLayer.parentLayerId = parentLayerId;
    }

    // Progress setup
    let progress = 0;
    const totalProgress = scenedata.layerMeshes.length;
    setProgress((progress / totalProgress) * 80 + 10);

    // Export each group
    for (let i = 0; i < scenedata.layerMeshes.length; i++) {
        // Check for cancellation
        throwIfCancelled();

        let layerGroupName = scenedata.layerGroupNames[i];
        let isSensorGroup = false;

        if (layerGroupName.startsWith('SensorData:')) {
            isSensorGroup = true;
            layerGroupName = layerGroupName.replace('SensorData:', '');
        }

        const groupLayerIndex = rhinoScene.layers().addLayer(layerGroupName, LAYER_COLOR);
        const groupLayer = rhinoScene.layers().findIndex(groupLayerIndex);
        const groupLayerId = groupLayer.id;
        groupLayer.parentLayerId = isSensorGroup ? sensorLayerId : parentLayerId;

        // Each “layerMeshes[i][j]” is one mesh group (THREE.Object3D) we traverse
        for (let j = 0; j < scenedata.layerMeshes[i].length; j++) {
            const layerIndex = rhinoScene.layers().addLayer(scenedata.layerNames[i][j], LAYER_COLOR);
            const layer = rhinoScene.layers().findIndex(layerIndex);
            layer.parentLayerId = groupLayerId;

            const jobs: THREE.Object3D[] = [];
            scenedata.layerMeshes[i][j].traverse((obj) => {
                if (obj.type === 'Mesh' || obj.type === 'LineSegments2') jobs.push(obj);
            });

            // Process jobs in chunks so cancel can run
            for (let k = 0; k < jobs.length; k++) {
                throwIfCancelled();

                const obj = jobs[k];
                if (obj.type === 'Mesh') {
                    await Export3DMMesh(
                        obj as THREE.Mesh,
                        rhinoScene,
                        layerIndex,
                        defaultBlackIndex,
                        throwIfCancelled,
                        sensorById,
                        valueUnit
                    );
                } else {
                    Export3DLines(obj as LineSegments2, rhinoScene, layerIndex);
                }

                // yield every N exported objects
                if (k % 10 === 0) await yieldToBrowser();
            }
        }

        progress++;
        setProgress((progress / totalProgress) * 80 + 10);
        await RepaintProgress();
    }

    const options = new rhino.File3dmWriteOptions();
    options.version = 7;

    return { formattedData: rhinoScene.toByteArray() as Uint8Array };
}
